import { useThree, useFrame } from '@react-three/fiber';
import React, { useContext, forwardRef, useRef, useCallback, useMemo, useLayoutEffect, createContext } from 'react';
import mergeRefs from 'react-merge-refs';
import { Quaternion, Vector3, Matrix4, Color, InstancedBufferAttribute, CustomBlending, AddEquation } from 'three';
import CustomShaderMaterial from 'three-custom-shader-material';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var _excluded$1 = ["maxParticles", "safetySize", "children", "material"];
new Quaternion();
var tmpScale = new Vector3();
var tmpMatrix4 = new Matrix4();
var components = {
  position: new Vector3(),
  quaternion: new Quaternion(),
  velocity: new Vector3(),
  acceleration: new Vector3(),
  delay: 0,
  lifetime: 1,
  scaleStart: new Vector3(),
  scaleEnd: new Vector3(),
  colorStart: new Color(),
  colorEnd: new Color(),
  alphaStart: 1,
  alphaEnd: 0
};
var ParticlesContext = /*#__PURE__*/createContext(null);
var useParticles = function useParticles() {
  return useContext(ParticlesContext);
};
var MeshParticles = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$maxParticles = _ref.maxParticles,
      maxParticles = _ref$maxParticles === void 0 ? 1000 : _ref$maxParticles,
      _ref$safetySize = _ref.safetySize,
      safetySize = _ref$safetySize === void 0 ? 100 : _ref$safetySize,
      children = _ref.children,
      material = _ref.material,
      props = _objectWithoutProperties(_ref, _excluded$1);

  /* The safetySize allows us to emit a batch of particles that would otherwise
  exceed the maximum instance count (which would make WebGL crash.) This way, we don't
  have to upload the entirety of all buffers every time the playhead wraps back to 0. */
  var maxInstanceCount = maxParticles + safetySize;
  var imesh = useRef(null);
  var playhead = useRef(0);

  var _useThree = useThree(),
      clock = _useThree.clock;
  /* Helper method to create new instanced buffer attributes */


  var createAttribute = useCallback(function (itemSize) {
    return new InstancedBufferAttribute(new Float32Array(maxInstanceCount * itemSize), itemSize);
  }, [maxInstanceCount]);
  /* Let's define a number of attributes. */

  var attributes = useMemo(function () {
    return {
      time: createAttribute(2),
      velocity: createAttribute(3),
      acceleration: createAttribute(3),
      colorStart: createAttribute(4),
      colorEnd: createAttribute(4),
      scaleStart: createAttribute(3),
      scaleEnd: createAttribute(3)
    };
  }, [maxInstanceCount]);
  /* Register the instance attributes with the imesh. */

  useLayoutEffect(function () {
    for (var key in attributes) {
      imesh.current.geometry.setAttribute(key, attributes[key]);
    }

    imesh.current.count = 0;
  }, [attributes]);
  var spawnParticle = useCallback(function (count, setup) {
    var instanceMatrix = imesh.current.instanceMatrix;
    /* Configure the attributes to upload only the updated parts to the GPU. */

    /* TODO: allow the user to call spawnParticles multiple times within the same frame */

    var allAttributes = [instanceMatrix].concat(_toConsumableArray(Object.values(attributes)));
    allAttributes.forEach(function (attribute) {
      attribute.needsUpdate = true;
      attribute.updateRange.offset = playhead.current * attribute.itemSize;
      attribute.updateRange.count = count * attribute.itemSize;
    });
    /* For every spawned particle, write some data into the attribute buffers. */

    for (var i = 0; i < count; i++) {
      var _attributes$velocity, _attributes$accelerat, _attributes$scaleStar, _attributes$scaleEnd;

      /* Reset components */
      components.position.set(0, 0, 0);
      components.quaternion.set(0, 0, 0, 1);
      components.velocity.set(0, 0, 0);
      components.acceleration.set(0, 0, 0);
      components.scaleStart.set(1, 1, 1);
      components.scaleEnd.set(1, 1, 1);
      components.delay = 0;
      components.lifetime = 1;
      components.colorStart.setRGB(1, 1, 1);
      components.colorEnd.setRGB(1, 1, 1);
      components.alphaStart = 1;
      components.alphaEnd = 0;
      /* Run setup */

      setup === null || setup === void 0 ? void 0 : setup(components);
      imesh.current.setMatrixAt(playhead.current, tmpMatrix4.compose(components.position, components.quaternion, tmpScale.setScalar(1)));
      /* Set times */

      attributes.time.setXY(playhead.current, clock.elapsedTime + components.delay, clock.elapsedTime + components.lifetime);
      /* Set velocity */

      (_attributes$velocity = attributes.velocity).setXYZ.apply(_attributes$velocity, [playhead.current].concat(_toConsumableArray(components.velocity.toArray())));
      /* Set acceleration */


      (_attributes$accelerat = attributes.acceleration).setXYZ.apply(_attributes$accelerat, [playhead.current].concat(_toConsumableArray(components.acceleration.toArray())));
      /* Set color */


      attributes.colorStart.setXYZW(playhead.current, components.colorStart.r, components.colorStart.g, components.colorStart.b, components.alphaStart);
      attributes.colorEnd.setXYZW(playhead.current, components.colorEnd.r, components.colorEnd.g, components.colorEnd.b, components.alphaEnd);
      /* Set scale */

      (_attributes$scaleStar = attributes.scaleStart).setXYZ.apply(_attributes$scaleStar, [playhead.current].concat(_toConsumableArray(components.scaleStart.toArray())));

      (_attributes$scaleEnd = attributes.scaleEnd).setXYZ.apply(_attributes$scaleEnd, [playhead.current].concat(_toConsumableArray(components.scaleEnd.toArray())));
      /* Advance playhead */


      playhead.current++;
    }
    /* Increase count of imesh to match playhead */


    if (playhead.current > imesh.current.count) {
      imesh.current.count = playhead.current;
    }
    /* If we've gone past the number of max particles, reset the playhead. */


    if (playhead.current > maxParticles) {
      playhead.current = 0;
    }
  }, [attributes]);
  /* Every frame, advance the time uniform */

  useFrame(function () {
    imesh.current.material.uniforms.u_time.value = clock.elapsedTime;
  });
  return /*#__PURE__*/React.createElement("instancedMesh", _extends({
    ref: mergeRefs([imesh, ref]),
    args: [undefined, material, maxInstanceCount]
  }, props), /*#__PURE__*/React.createElement(ParticlesContext.Provider, {
    value: {
      spawnParticle: spawnParticle
    }
  }, children));
});

function getValue(fov) {
  return typeof fov === "function" ? fov() : fov;
}

var Emitter = function Emitter(_ref) {
  var _ref$initialParticles = _ref.initialParticles,
      initialParticles = _ref$initialParticles === void 0 ? 0 : _ref$initialParticles,
      _ref$burstParticles = _ref.burstParticles,
      burstParticles = _ref$burstParticles === void 0 ? 1 : _ref$burstParticles,
      _ref$initialDelay = _ref.initialDelay,
      initialDelay = _ref$initialDelay === void 0 ? 0 : _ref$initialDelay,
      _ref$burstCount = _ref.burstCount,
      burstCount = _ref$burstCount === void 0 ? 0 : _ref$burstCount,
      _ref$burstDelay = _ref.burstDelay,
      burstDelay = _ref$burstDelay === void 0 ? 0 : _ref$burstDelay,
      setup = _ref.setup;
  var initialCooldown = useRef(getValue(initialDelay));
  var burstCooldown = useRef(getValue(burstDelay));
  var burstsRemaining = useRef(getValue(burstCount));

  var _useParticles = useParticles(),
      spawnParticle = _useParticles.spawnParticle;

  useFrame(function (_, dt) {
    /* First of all, wait the initial cooldown */
    if (initialCooldown.current >= 0) {
      initialCooldown.current -= dt;

      if (initialCooldown.current <= 0) {
        spawnParticle(getValue(initialParticles), setup);
      }

      return;
    }
    /* Now we can deal with the remaining bursts */


    if (burstsRemaining.current > 0) {
      burstCooldown.current -= dt;
      /* If we've reached the end of the cooldown, spawn some particles */

      if (burstCooldown.current <= 0) {
        spawnParticle(getValue(burstParticles), setup);
        /* If there are bursts left, reset the cooldown */

        if (burstsRemaining.current > 0) {
          burstCooldown.current += getValue(burstDelay);
          burstsRemaining.current--;
        }
      }
    }
  });
  return null;
};

/* Uniforms */
var uniforms =
/*glsl*/
"\nuniform float u_time;\nuniform bool u_billboard;\n";
/* Varyings */

var varyings =
/*glsl*/
"\nvarying float v_timeStart;\nvarying float v_timeEnd;\nvarying float v_progress;\nvarying float v_age;\nvarying vec4 v_colorStart;\nvarying vec4 v_colorEnd;\n";
/* Attributes */

var attributes =
/*glsl*/
"\nattribute vec2 time;\nattribute vec3 velocity;\nattribute vec3 acceleration;\nattribute vec4 colorStart;\nattribute vec4 colorEnd;\nattribute vec3 scaleStart;\nattribute vec3 scaleEnd;\n";
/* Vertex Shader */

var vertexShader =
/* glsl */
"\n".concat(uniforms, "\n").concat(varyings, "\n").concat(attributes, "\n\n/* Billboard helper */\nvec3 billboard(vec2 v, mat4 view){\n   vec3 up = vec3(view[0][1], view[1][1], view[2][1]);\n   vec3 right = vec3(view[0][0], view[1][0], view[2][0]);\n   vec3 p = right * v.x + up * v.y;\n   return p;\n}\n\n/* Set the varyings we want to forward */\nvoid setVaryings() {\n  v_timeStart = time.x;\n  v_timeEnd = time.y;\n  v_colorStart = colorStart;\n  v_colorEnd = colorEnd;\n  v_age = u_time - v_timeStart;\n  v_progress = v_age / (v_timeEnd - v_timeStart);\n}\n\nvoid main() {\n  setVaryings();\n\n  /* Apply Billboarding */\n  if (u_billboard) {\n    csm_Position = billboard(csm_Position.xy, viewMatrix);\n  }\n\n  /* Start with an origin offset */\n  vec3 offset = vec3(0.0, 0.0, 0.0);\n\n  /* Apply velocity and acceleration */\n  offset += vec3(v_age * velocity + 0.5 * v_age * v_age * acceleration);\n\n  /* Apply scale */\n  csm_Position *= mix(scaleStart, scaleEnd, v_progress);\n\n  /* Apply the instance matrix. */\n  offset *= mat3(instanceMatrix);\n\n  /* Apply the offset */\n  csm_Position += offset;\n}\n\n");
/* Fragment Shader */

var fragmentShader =
/* glsl */
"\n".concat(uniforms, "\n").concat(varyings, "\n\nvoid main() {\n  /* Lifetime management: discard this instance if it is not in the current time range */\n  if (u_time < v_timeStart || u_time > v_timeEnd) {\n    discard;\n  }\n\n  /* Get diffuse color */\n  vec4 diffuse4 = vec4(diffuse, 1.0);\n\n  /* Mix diffuse color with the animation color */\n  csm_DiffuseColor = mix(diffuse4 * v_colorStart, diffuse4 * v_colorEnd, v_progress);\n\n  /* Apply the diffuse color */\n  csm_FragColor = csm_DiffuseColor;\n\n  /* Mix in the texture */\n  #ifdef USE_MAP\n    csm_FragColor *= texture2D(map, vUv);\n  #endif\n}\n");

var shader = /*#__PURE__*/Object.freeze({
  __proto__: null,
  vertexShader: vertexShader,
  fragmentShader: fragmentShader
});

var _excluded = ["billboard"];
var ParticlesMaterial = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$billboard = _ref.billboard,
      billboard = _ref$billboard === void 0 ? false : _ref$billboard,
      props = _objectWithoutProperties(_ref, _excluded);

  var uniforms = useMemo(function () {
    return {
      u_time: {
        value: 0
      },
      u_billboard: {
        value: billboard
      }
    };
  }, []);
  return /*#__PURE__*/React.createElement(CustomShaderMaterial, _extends({
    ref: ref,
    uniforms: uniforms,
    blending: CustomBlending,
    blendEquation: AddEquation,
    depthTest: true,
    depthWrite: false
  }, shader, props));
});

export { Emitter, MeshParticles, ParticlesMaterial, useParticles };
